import { Logger } from "../helpers";

/**
 * Cookie class manages setting, getting, and updating consent data via browser cookies.
 */
class Cookie {
  constructor() {
    // Initialize consent data
    this.consentData = this.initializeConsentData();
    // Check for the GPC signal from the browser
    this.processGlobalPrivacyControlSignal();
    // Check if we need to clear cookies from a consent change
    if ( this.hasClearCookies() ) {
      Logger.log("Clearing cookies as per consent change.");
      this.clearCookies();
      // Set the flag to false after clearing
      this.setConsentData({ clearCookies: false });
    }
  }

  // Fetch existing consent data or set default values  
  initializeConsentData() {
    const consentCookie = this.getCookie("consentData");
    if (consentCookie) {
      try {
        const decoded = decodeURIComponent(consentCookie);
        const parsed = JSON.parse(decoded);
  
        // Normalize stringified booleans (just in case)
        parsed.hasOptedOut = parsed.hasOptedOut === true || parsed.hasOptedOut === "1" || parsed.hasOptedOut === 1;
        parsed.hasInteracted = parsed.hasInteracted === true || parsed.hasInteracted === "1" || parsed.hasInteracted === 1;
  
        Logger.log("Consent data from cookie:", parsed);
        return parsed;
      } catch (err) {
        Logger.error("Error parsing consentData cookie:", err);
      }
    }
  
    Logger.log("Consent cookie not found or invalid, defaulting to opt-in");
    return { hasOptedOut: false, hasInteracted: false };
  }
  
  // Process globalPrivacyControl signal
  processGlobalPrivacyControlSignal() {
    const gpc = navigator.globalPrivacyControl;
    if ( gpc ) {
      Logger.log("GPC signal detected, opting out.");
      // If GPC was false or undefined, clear cookies
      if ( !this.hasGPCSignal() ) {
        Logger.log("GPC signal was not previously detected, clearing cookies.");
        this.clearCookies();
      }
      // Set the consent data to opted out and signal true
      this.setConsentData({ hasOptedOut: true, gpcSignal: true });
    } else {
      Logger.log("No GPC signal detected.");
      this.setConsentData({ gpcSignal: false });
    }
  }

  // Update consent data and store in cookie
  setConsentData(updatedData) {
    const newConsentData = { ...this.consentData, ...updatedData };
    this.consentData = newConsentData;
    this.setCookie("consentData", JSON.stringify(newConsentData), 365);
  }

  //Checks if the user has opted out.
  hasOptedOut() {
    return this.consentData.hasOptedOut === true;
  }

  //Checks if the user has interacted with the banner.
  hasInteracted() {
    return this.consentData.hasInteracted === true;
  }

  // Check if the user had the GPC signal
  hasGPCSignal() {
    return this.consentData.gpcSignal === true;
  }

  // Check if the user had an auto Geo opt-out
  hasGeoOptout() {
    return this.consentData.geoOptout === true;
  }

  // Check if the system has asked us to clear cookies
  hasClearCookies() {
    return this.consentData.clearCookies === true;
  }


  //Removes the banner from the DOM if it exists.
  removeBanner() {
    Logger.log("Removing consent banner from DOM.");
    const banner = document.getElementById("cookie-consent-banner");
    if (banner) banner.remove();
  }

  // Write cookie with expiration and security flags
  setCookie(name, value, days, secure = true, sameSite = 'Lax', httpOnly = false) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    let cookieString = `${name}=${value}; ${expires}; path=/`;
    if (secure) cookieString += '; Secure';
    if (sameSite) cookieString += `; SameSite=${sameSite}`;
    if (httpOnly) cookieString += '; HttpOnly';
    document.cookie = cookieString;
  }

  // Retrieves a cookie value by name.
  getCookie(name) {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(";");
    for (let c of ca) {
      while (c.charAt(0) === " ") c = c.substring(1);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
    }
    return null;
  }

  // Clear all cookies except the specified ones
  clearCookies() {
    const cookiesToKeep = new Set(["consentData", "legal-banner-dismissed", "PHPSESSID"]);
    const cookies = document.cookie.split("; ");
    const domain = window.location.hostname;
    const path = "/"; // Default path for cookies

    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      if ( !cookiesToKeep.has(name) ) {
        Logger.log(`Clearing cookie: ${name}`);
        // Try to clear the cookie for root and subdomains
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=.${domain}`;
      }
    });
  }
}

/**
 * CookieBanner extends Cookie to provide UI rendering and interactions.
 */
class CookieBanner extends Cookie {
  constructor(options = {}) {
    super();

    // Decode HTML entities
    const decodeHtml = (html) => {
      const txt = document.createElement("textarea");
      txt.innerHTML = html;
      return txt.value;
    };

    // Read translation data injected via PHP (as JSON)
    const translationNode = document.getElementById("cookie-banner-text");
    let bannerData = {};
    if (translationNode) {
      try {
        bannerData = JSON.parse(translationNode.textContent);
      } catch (error) {
        Logger.log("Failed to parse cookie banner data", error);
      }
    }

    // Populate final config with fallback to defaults
    this.options = {
      message: bannerData.message || "Default cookie message.",
      acceptButton: bannerData.acceptButton || "Accept",
      declineButton: bannerData.declineButton || "Decline",
      closeButton: decodeHtml("&#x2715;"), // Static close button
      gpcMessage: bannerData.gpcMessage || "Your browser has sent a Global Privacy Control signal — we’ve honored that by limiting data sharing and disabling optional cookies. Learn more in our Privacy Policy.",
    
      // Dynamic links from backend
      privacyPolicyLink: bannerData.privacyLink || "/privacy-policy/",
      termsOfUseLink: bannerData.termsLink || "/terms-of-use/",
    
      doNotSellLink: "#do-not-sell-link", // Keeps this as-is for interaction
    
      ...options, // Allow for any overrides
    };
  }

  // Initialize banner if user hasn't interacted
  async initialize() {
    Logger.log("Initializing Cookies Tool");

    // Create banner if the user has not already interacted with the banner and doesn't have the GPC signal or geoOptout
    if ( !this.hasInteracted() && !this.hasGPCSignal() && !this.hasGeoOptout() ) {
      Logger.log("Initializing Cookies Banner");
      this.createBanner();
    }
    // Attach event listeners for "Do Not Sell" link to prompt banner
    this.attachDoNotSellListener();
  }

  // Build the banner HTML and inject into DOM
  createBanner() {
    Logger.log("Creating consent banner");
    this.banner = document.createElement("div");
    this.banner.id = "cookie-consent-banner";

    // Close button
    const closeButton = document.createElement("a");
    closeButton.classList.add("close-button");
    closeButton.textContent = this.options.closeButton;
    closeButton.addEventListener("click", this.handleClose.bind(this));


    // Content container
    const contentContainer = document.createElement("div");
    contentContainer.classList.add("banner-content");

    // Consent message
    const message = document.createElement("div");
    message.classList.add("privacy-message");
    // Set the message based on GPC signal
    if ( this.hasGPCSignal() ) {
      message.innerHTML = this.options.gpcMessage;
    } else {
      message.innerHTML = this.options.message;
    }
    // Append message to content container
    contentContainer.appendChild(message);

    // Buttons (Accept and Decline), if GPC signal is not present
    if ( !this.hasGPCSignal() ) {
      const buttonContainer = document.createElement("div");
      buttonContainer.classList.add("button-container");
      // Accept button
      const acceptButton = this.createButton(
        this.options.acceptButton,
        "accept-cookies",
        () => this.handleConsent(false)
      );
      // Decline button
      const declineButton = this.createButton(
        this.options.declineButton,
        "decline-cookies",
        () => this.handleConsent(true)
      );
      // Append buttons to button container and then to content container
      buttonContainer.appendChild(acceptButton);
      buttonContainer.appendChild(declineButton);
      contentContainer.appendChild(buttonContainer);
    }
    
    // Append close button and content to banner
    this.banner.appendChild(closeButton);
    this.banner.appendChild(contentContainer);
    // Append banner to body
    document.body.appendChild(this.banner);
  }

  // Handle close action from close button
  handleClose(evt) {
    evt.preventDefault();
    Logger.log("User closed banner, marking as interacted.");
    this.setConsentData({ hasInteracted: true });
    this.removeBanner();
  }

  // Handle user consent action
  handleConsent(hasOptedOut) {
    Logger.log(`User opted out: ${hasOptedOut}`);
    this.setConsentData({ hasOptedOut, hasInteracted: true });
    // Fire a window event that consent has been updated
    window.dispatchEvent(new Event("consent-updated"));

    // Remove the banner from the DOM
    this.removeBanner();
    // Clear cookies if opting out
    if ( hasOptedOut ) {
      // Clear all cookies on next page load
      this.setConsentData({ clearCookies: true });
    }
  }
  
  // Build buttons with styles and logic
  createButton(text, id, onClick) {
    const button = document.createElement("button");
    button.textContent = text;
    button.id = id;
    button.classList.add("privacy-button", "new-button", "button--green", "button--small");
    button.addEventListener("click", onClick);
    return button;
  }

  // Attach logic for "Do Not Sell" links to reopen banner
  attachDoNotSellListener() {
    document.querySelectorAll(this.options.doNotSellLink).forEach(link => {
      link.addEventListener("click", event => {
        event.preventDefault();
        Logger.log("User clicked 'Do Not Sell' - Showing banner.");
        if (!document.getElementById("cookie-consent-banner")) {
          this.createBanner();
        }
      });
    });
  }
}

// Export CookieBanner class for global Analytics integration
export { CookieBanner };
